import type {LocationInfoDescriptionData} from "@reva/shared/description-keywords";
import type {LocationInfoTimelinesData} from "@reva/shared/timelines";

import type ServiceApi from "../../api";
import {isCurrentEnvironmentInDevelopMode} from "../../environment";
import {allGroundValueServicesKnown} from "../../util/groundValueServices";
import {AvmApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {AvmApiLocationInfoClimateRisk} from "../payload-types/locationinfo-climate-risk";
import type {AvmApiLocationInfoEmission} from "../payload-types/locationinfo-emission";
import type {AvmApiLocationInfoMicroMacro} from "../payload-types/locationinfo-micro-macro";
import type {AvmApiLocationInfoNoise} from "../payload-types/locationinfo-noise";
import type {AvmApiLocationInfoPois} from "../payload-types/locationinfo-pois";
import type {AvmApiLocationInfoPriceIndex} from "../payload-types/locationinfo-priceindex";
import type {AvmApiLocationInfoStandardGroundValue} from "../payload-types/locationinfo-standard-ground-value";
import {removeResponseValue} from "../response";
import {ValidationErrorI18n} from "../validation";

/**
 * All available API Endpoints (URL segments)
 */
enum LocationInfoApiEndpoint {
    CLIMATE_RISK = "climateRisk",
    EMISSION = "emission",
    MICRO_MACRO = "microMacro",
    // #19165: löst den alten Endpunkt "microMacroText" ab; liefert zusätzlich market_text (und künftig meso_text)
    DESCRIPTION = "description",
    MUNICIPALITY_TYPE = "municipalityType",
    NEIGHBORHOOD = "neighborhood",
    NOISE = "noise",
    POIS = "pois",
    PRICE_INDEX = "priceIndex",
    STANDARD_GROUND_VALUE = "standardGroundValue",
    TIMELINES = "timelines",
}

export abstract class LocationInfoApiInterface<Result> extends SimpleApiInterface<Result> {
    endpoint: LocationInfoApiEndpoint;
    address: string;
    revaId: string | null;
    objectId: string | null;
    accounting: string | null;

    protected constructor(
        endpoint: LocationInfoApiEndpoint,
        address: string,
        revaId: string | null,
        objectId?: string | null,
        accounting?: string | null,
    ) {
        super();
        this.endpoint = endpoint;
        this.address = address;
        this.revaId = revaId;
        this.objectId = objectId ?? null;
        this.accounting = accounting ?? null;
    }

    protected async executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<Result> {
        const apiResult = await executor.fetchGET(url, credentials);
        return this.removeUnusedAddress(apiResult);
    }

    /** Remove unused `address` property from LocationInfo response */
    protected removeUnusedAddress(apiResult: any): any {
        return removeResponseValue(apiResult, "address");
    }

    protected validate() {
        if (!this.address || this.address.length < 4) {
            throw new ValidationErrorI18n("address", "address.error.too_short");
        }
        if (!this.revaId && isCurrentEnvironmentInDevelopMode()) {
            console.warn(`Execution of LocationInfo ${this.endpoint} without a revaId!`);
        }
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl(this.buildApiPath(), {address: this.address, ...this.buildMetaQueryParams()});
    }

    protected buildApiPath(): string[] {
        return [AvmApi.PATH_LOCATIONINFO, this.endpoint];
    }

    protected buildMetaQueryParams(): Record<string, any> {
        return {
            revaId: this.revaId,
            object_id: this.objectId,
            accounting: this.accounting,
        };
    }
}

export class LocationInfoPriceIndexApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoPriceIndex> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.PRICE_INDEX, address, revaId, objectId, accounting);
    }
}

export class LocationInfoClimateRiskApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoClimateRisk> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.CLIMATE_RISK, address, revaId, objectId, accounting);
    }
}

export class LocationInfoEmissionApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoEmission> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.EMISSION, address, revaId, objectId, accounting);
    }
}

export class LocationInfoMicroMacroApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoMicroMacro> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.MICRO_MACRO, address, revaId, objectId, accounting);
    }
}

export class LocationInfoNoiseApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoNoise> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.NOISE, address, revaId, objectId, accounting);
    }
}

export class LocationInfoPoisApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoPois> {
    private readonly maxPoisPerType: number | null;
    constructor(
        address: string,
        revaId: string | null,
        objectId?: string | null,
        accounting?: string | null,
        maxPoisPerType = 1,
    ) {
        super(LocationInfoApiEndpoint.POIS, address, revaId, objectId, accounting);
        this.maxPoisPerType = maxPoisPerType;
    }
    protected override buildMetaQueryParams(): Record<string, any> {
        return {
            ...super.buildMetaQueryParams(),
            ...(this.maxPoisPerType != null ? {maxRank: this.maxPoisPerType} : {}),
        };
    }
}

export class LocationInfoDescriptionApiInterface extends LocationInfoApiInterface<LocationInfoDescriptionData> {
    private readonly locationIndex: number | null;

    constructor(
        address: string,
        revaId: string | null,
        locationIndex?: number | null,
        objectId?: string | null,
        accounting?: string | null,
    ) {
        super(LocationInfoApiEndpoint.DESCRIPTION, address, revaId, objectId, accounting);
        this.locationIndex = locationIndex ?? null;
    }

    protected override buildMetaQueryParams(): Record<string, any> {
        return {
            ...super.buildMetaQueryParams(),
            ...(this.locationIndex != null ? {ratingOfLocation: this.locationIndex} : {}),
        };
    }
}

export class LocationInfoStandardGroundValueApiInterface extends LocationInfoApiInterface<AvmApiLocationInfoStandardGroundValue> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.STANDARD_GROUND_VALUE, address, revaId, objectId, accounting);
    }

    async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<AvmApiLocationInfoStandardGroundValue> {
        const apiResult = await executor.fetchGET(url, credentials);
        const result: AvmApiLocationInfoStandardGroundValue = this.removeUnusedAddress(apiResult);
        // Prüfe bei Bodenrichtwerten, ob für jeden Dienst copyright infos hinterlegt sind
        if (Array.isArray(result?.list) && !allGroundValueServicesKnown(result.list)) {
            throw new ValidationErrorI18n("standardGroundValue", "object.groundvalue.validationError.missingLicense");
        }
        return result;
    }
}

/**
 * Timelines are the only LocationInfo endpoint, which diverges from the default
 * `GET /v1/locationInformation/$endpoint?address=address` pattern.
 */
export class LocationInfoTimelinesApiInterface extends LocationInfoApiInterface<LocationInfoTimelinesData> {
    constructor(address: string, revaId: string | null, objectId?: string | null, accounting?: string | null) {
        super(LocationInfoApiEndpoint.TIMELINES, address, revaId, objectId, accounting);
    }

    async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<LocationInfoTimelinesData> {
        const apiResult = await executor.fetchPOST(url, credentials, this.buildPayload());
        return this.dropUnnecessaryData(this.removeUnusedAddress(apiResult));
    }

    protected buildUrl(api: ServiceApi): string {
        // No address query param, instead address is given as POST payload
        return api.buildAvmApiUrl(this.buildApiPath(), this.buildMetaQueryParams());
    }

    protected buildPayload(): any {
        return {
            address: this.address,
            periods: 1,
            year_of_construction_category: 0,
            object_id: this.objectId ?? undefined,
            accounting: this.accounting ?? undefined,
        };
    }

    protected dropUnnecessaryData(result: LocationInfoTimelinesData): LocationInfoTimelinesData {
        const {info_hint, info_hint_de, timelines_trend} = result;
        return {
            info_hint,
            info_hint_de,
            timelines_trend: timelines_trend.map((trendItem) => {
                const {segment, regional_level, data} = trendItem;
                return {
                    segment,
                    regional_level,
                    data: data.map((dataItem) => {
                        // extract only relevant keys, ignore all additional (unused) keys coming back from the API
                        const {
                            start_of_period,
                            price,
                            price_low,
                            price_high,
                            price_offer,
                            price_offer_low,
                            price_offer_high,
                        } = dataItem;
                        return {
                            start_of_period,
                            price,
                            price_low,
                            price_high,
                            price_offer,
                            price_offer_low,
                            price_offer_high,
                        };
                    }),
                };
            }),
        };
    }
}
